/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-javascript-auth-sample',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './javascript-auth-sample.component.html',
  styleUrl: './javascript-auth-sample.component.scss'
})
export class JavascriptAuthSampleComponent {
  sample = {
    id: 'javascript-auth',
    title: 'Athena Integration - JavaScript Authentication Demo',
    shortDescription: 'Complete authentication workflow using MSAL for accessing Athena APIs with JavaScript.',
    fullDescription: `This project demonstrates how to implement **Microsoft Authentication Library (MSAL)** integration to authenticate and authorize users for accessing **Athena APIs**. Athena serves as a centralized API gateway that provides secure access to various backend services including IoT devices, GCP products, and data mapping services.

The Athena Integration project showcases a complete authentication workflow that enables applications to:

1. **Authenticate users** using Azure AD B2C through MSAL
2. **Acquire access tokens** for Athena API authorization
3. **Make authenticated requests** to various backend services through Athena
4. **Handle token lifecycle** including automatic refresh and error handling

Built with **JavaScript**, it provides a modular, reusable architecture for integrating Athena authentication into any web application.`,
    code: `// JavaScript Athena Service Integration
import { AthenaAuthService } from './athena-auth.js';
import { AthenaConfig } from './athena-config.js';

const config = new AthenaConfig({
  clientId: 'your-client-id',
  authority: 'https://your-tenant.b2clogin.com',
  redirectUri: 'http://localhost:3000'
});

const authService = new AthenaAuthService(config);

// Authenticate user
await authService.login();

// Get access token
const token = await authService.getAccessToken();

// Make authenticated request
const response = await fetch('https://athena-api/v1/devices', {
  headers: { 'Authorization': \`Bearer \${token}\` }
});`,
    technologies: ['JavaScript', 'MSAL', 'Azure AD B2C', 'Athena API']
  };
}
